(function (global) {
  'use strict';

  const asString = (value) => value == null ? '' : String(value);
  const patternsMatch = (value, patterns) => (patterns || []).some((pattern) => pattern.test(value));

  function findOptionIndex(product, patterns) {
    return (product?.options || []).findIndex((name) => patternsMatch(asString(name), patterns));
  }

  function variantOption(variant, index) {
    if (index < 0) return '';
    return asString(variant?.options?.[index]);
  }

  function normalizeVariant(product, variant) {
    return {
      ...variant,
      id: asString(variant.id),
      productId: asString(product.id),
      productTitle: product.title || '',
      productHandle: product.handle || '',
      image: variant.image || product.featuredImage || '',
    };
  }

  function flattenProducts(products) {
    return (products || []).flatMap((product) =>
      (product.variants || []).map((variant) => normalizeVariant(product, variant))
    );
  }

  function inventoryStatus(variant, requested = 1) {
    const quantity = Number(variant?.inventoryQuantity);
    const tracked = Boolean(variant?.inventoryManagement);
    const continueSelling = asString(variant?.inventoryPolicy).toLowerCase() === 'continue';
    const availableNow = tracked && Number.isFinite(quantity) ? Math.max(0, quantity) : null;
    const allowed = !tracked || continueSelling || (Boolean(variant?.available) && availableNow >= requested);
    return {
      requested,
      tracked,
      continueSelling,
      availableNow,
      toOrder: availableNow == null ? 0 : Math.max(0, requested - availableNow),
      allowed,
    };
  }

  function simpleChoices(products, patterns) {
    return (products || []).flatMap((product) => {
      const labelIndex = findOptionIndex(product, patterns);
      return (product.variants || []).map((variant) => {
        const row = normalizeVariant(product, variant);
        const status = inventoryStatus(row, 1);
        return {
          id: row.id,
          title: variantOption(row, labelIndex) || row.title || product.title,
          subtitle: product.title || '',
          image: row.image,
          price: Number(row.price) || 0,
          variant: row,
          status,
          available: status.allowed,
        };
      });
    });
  }

  function selectedVariant(products, variantId) {
    return flattenProducts(products).find((variant) => variant.id === asString(variantId)) || null;
  }

  function createState(programId = '') {
    return {
      programId: asString(programId),
      buckleVariantId: '',
      strapVariantId: '',
      sizing: 'uncut',
      cutSize: '',
      acknowledged: false,
    };
  }

  function validate({ state, program, catalog }) {
    const errors = [];
    if (!program) errors.push('Choose a belt program.');
    const buckle = selectedVariant(catalog?.buckles, state?.buckleVariantId);
    const strap = selectedVariant(catalog?.straps, state?.strapVariantId);
    if (!buckle) errors.push('Choose a buckle.');
    if (!strap) errors.push('Choose a strap.');
    if (buckle && !inventoryStatus(buckle, 1).allowed) errors.push('The selected buckle is unavailable.');
    if (strap && !inventoryStatus(strap, 1).allowed) errors.push('The selected strap is unavailable.');
    if (state?.sizing === 'cut' && !state?.cutSize) errors.push('Choose a cut size.');
    if (state?.sizing === 'cut' && !state?.acknowledged) errors.push('Acknowledge the final-sale warning.');
    return { valid: errors.length === 0, errors, buckle, strap };
  }

  function buildCartPlan({ state, program, catalog }) {
    const result = validate({ state, program, catalog });
    if (!result.valid) throw new Error(result.errors.join(' '));
    const beltId = `PMS-BELT-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const sizing = state.sizing === 'cut' ? `Cut to ${state.cutSize}` : 'Leave uncut';
    const metadata = {
      beltId,
      programId: program.id,
      programTitle: program.title,
      sizing,
      finalSale: state.sizing === 'cut' ? 'Acknowledged' : '',
    };
    const items = [
      { variantId: result.buckle.id, quantity: 1, component: 'Buckle', metadata },
      { variantId: result.strap.id, quantity: 1, component: 'Strap', metadata },
    ];
    return {
      beltId,
      programId: program.id,
      programTitle: program.title,
      sizing,
      retailTotal: Number(result.buckle.price || 0) + Number(result.strap.price || 0),
      items,
    };
  }

  global.PMSBeltBuilderCore = {
    createState,
    simpleChoices,
    selectedVariant,
    inventoryStatus,
    validate,
    buildCartPlan,
  };
})(typeof window !== 'undefined' ? window : globalThis);
